// src/components/ui/ConfirmDialog.js
// Pop-up de confirmation reutilisable (remplace window.confirm).
// Icone, titre, message, boutons Annuler/Confirmer. Variantes danger/primary.
// Fermeture : bouton, fond, Echap. react-icons, aucun emoji.
// Maxula

import React, { useEffect } from 'react';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

const PALETTE = {
  danger: { ring: 'bg-red-100 text-red-600', btn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500' },
  primary: { ring: 'bg-pink-100 text-[#e11d7b]', btn: 'bg-[#e11d7b] hover:opacity-90 focus:ring-pink-400' },
  warning: { ring: 'bg-amber-100 text-amber-600', btn: 'bg-amber-500 hover:bg-amber-600 focus:ring-amber-400' },
};

export default function ConfirmDialog({
  open,
  title = 'Confirmer',
  message,
  confirmLabel = 'Confirmer',
  busyLabel,
  cancelLabel = 'Annuler',
  variant = 'danger',
  icon: Icon = FiAlertTriangle,
  busy = false,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel && onCancel(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, busy, onCancel]);

  if (!open) return null;
  const p = PALETTE[variant] || PALETTE.danger;
  const close = () => { if (!busy) onCancel && onCancel(); };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/60"
      onClick={close}
      role="dialog"
      aria-modal="true"
    >
      <style>{`@keyframes cd_pop{0%{opacity:0;transform:translateY(10px) scale(.96)}100%{opacity:1;transform:none}}`}</style>
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'cd_pop .18s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${p.ring}`}>
              <Icon size={24} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              {message ? <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{message}</p> : null}
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Fermer"
              className="shrink-0 -mt-1 -mr-1 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <FiX size={18} />
            </button>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-70 ${p.btn}`}
            >
              {busy && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              {busy ? (busyLabel || confirmLabel) : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
